import React from 'react';
import {IntlShape, useIntl} from 'react-intl';
import {type TooltipFormulaItem} from '@/components/TooltipFormulaRenderer';
import {FinancialCategoryTooltip} from '@/forecast-app/shared/components/tooltips/FinancialCategoryTooltip';

type FormatMessage = IntlShape['formatMessage'];

const ActualMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_margin'}),
		description: formatMessage({id: 'financial.program.actual_margin.tooltip.description'}),
		details: [formatMessage({id: 'common.actual_profit'}), '/', formatMessage({id: 'common.actual_revenue'})],
	},
];

const ProjectedTotalMarginTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_margin'}),
		description: formatMessage({id: 'financial.program.projected_total_margin.tooltip.description'}),
		details: [
			formatMessage({id: 'common.projected_total_profit'}),
			'/',
			formatMessage({id: 'common.projected_total_revenue'}),
		],
	},
];

const ActualRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_revenue'}),
		descriptionWithHTML: {
			translationId: 'financial.program.actual_revenue.tooltip.description',
		},
	},
];

const VarianceWithCapActualRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'program_budget.variance_with_cap'}),
		description: formatMessage({id: 'financial.program.variance_with_cap.actual_revenue.tooltip.description'}),
		details: [
			formatMessage({id: 'financial.program.program_cap_amount'}),
			'-',
			formatMessage({id: 'common.actual_revenue'}),
		],
	},
];

const ProjectedTotalRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_revenue'}),
		descriptionWithHTML: {
			translationId: 'financial.program.projected_total_revenue.tooltip.description',
		},
		details: [formatMessage({id: 'common.actual_revenue'}), '+', formatMessage({id: 'common.remaining_work_revenue'})],
	},
];

const VarianceWithCapProjectedTotalRevenueTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'program_budget.variance_with_cap'}),
		description: formatMessage({
			id: 'financial.program.variance_with_cap.projected_total_revenue.tooltip.description',
		}),
		details: [
			formatMessage({id: 'financial.program.program_cap_amount'}),
			'-',
			formatMessage({id: 'common.projected_total_revenue'}),
		],
	},
];

const RemainingBudgetTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.remaining_budget'}),
		details: [formatMessage({id: 'common.fixed_price'}), '-', formatMessage({id: 'common.actual_revenue'})],
	},
];

const ActualValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.actual_value_of_service'}),
		description: formatMessage({id: 'financial.program.actual_value_of_service.tooltip.description'}),
		details: [
			formatMessage({id: 'common.actual_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.past_expenses'}),
		],
	},
];

const VarianceWithCapActualValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'program_budget.variance_with_cap'}),
		description: formatMessage({
			id: 'financial.program.variance_with_cap.actual_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'financial.program.program_cap_amount'}),
			'-',
			formatMessage({id: 'common.actual_value_of_service'}),
		],
	},
];

const ProjectedTotalValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'common.projected_total_value_of_service'}),
		description: formatMessage({id: 'financial.program.projected_total_value_of_service.tooltip.description'}),
		details: [
			formatMessage({id: 'common.projected_total_hours'}),
			'x',
			formatMessage({id: 'common.rate'}),
			'+',
			formatMessage({id: 'common.all_expenses'}),
		],
	},
];

const VarianceWithCapProjectedTotalValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'program_budget.variance_with_cap'}),
		description: formatMessage({
			id: 'financial.program.variance_with_cap.projected_total_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'financial.program.program_cap_amount'}),
			'-',
			formatMessage({id: 'common.projected_total_value_of_service'}),
		],
	},
];

const VarianceWithFixedPriceActualValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'program_budget.variance_with_fixed_price'}),
		description: formatMessage({
			id: 'financial.program.variance_with_fixed_price.actual_value_of_service.tooltip.description',
		}),
		details: [formatMessage({id: 'common.fixed_price'}), '-', formatMessage({id: 'common.actual_value_of_service'})],
	},
];

const VarianceWithFixedPriceProjectedTotalValueOfServiceTooltipItems = (formatMessage: FormatMessage): TooltipFormulaItem[] => [
	{
		title: formatMessage({id: 'program_budget.variance_with_fixed_price'}),
		description: formatMessage({
			id: 'financial.program.variance_with_fixed_price.projected_total_value_of_service.tooltip.description',
		}),
		details: [
			formatMessage({id: 'common.fixed_price'}),
			'-',
			formatMessage({id: 'common.projected_total_value_of_service'}),
		],
	},
];

const createTooltip = <T,>(itemsFunction: (formatMessage: FormatMessage, props: T) => TooltipFormulaItem[]): React.FC<T> => {
	return (props: React.PropsWithChildren<T>) => {
		const {formatMessage} = useIntl();

		const items = itemsFunction(formatMessage, props as T);

		return <FinancialCategoryTooltip {...props} size={'l'} items={items} />;
	};
};

export const ProgramHeaderTooltips = {
	ActualMarginTooltip: createTooltip(ActualMarginTooltipItems),
	ProjectedTotalMarginTooltip: createTooltip(ProjectedTotalMarginTooltipItems),
	ActualRevenueTooltip: createTooltip(ActualRevenueTooltipItems),
	VarianceWithCapActualRevenueTooltip: createTooltip(VarianceWithCapActualRevenueTooltipItems),
	ProjectedTotalRevenueTooltip: createTooltip(ProjectedTotalRevenueTooltipItems),
	VarianceWithCapProjectedTotalRevenueTooltip: createTooltip(VarianceWithCapProjectedTotalRevenueTooltipItems),
	RemainingBudgetTooltip: createTooltip(RemainingBudgetTooltipItems),
	ActualValueOfServiceTooltip: createTooltip(ActualValueOfServiceTooltipItems),
	VarianceWithCapActualValueOfServiceTooltip: createTooltip(VarianceWithCapActualValueOfServiceTooltipItems),
	ProjectedTotalValueOfServiceTooltip: createTooltip(ProjectedTotalValueOfServiceTooltipItems),
	VarianceWithCapProjectedTotalValueOfServiceTooltip: createTooltip(VarianceWithCapProjectedTotalValueOfServiceTooltipItems),
	VarianceWithFixedPriceActualValueOfServiceTooltip: createTooltip(VarianceWithFixedPriceActualValueOfServiceTooltipItems),
	VarianceWithFixedPriceProjectedTotalValueOfServiceTooltip: createTooltip(
		VarianceWithFixedPriceProjectedTotalValueOfServiceTooltipItems
	),
};
